import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const CarrierProfileEditPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    carrierName: '',
    mcNumber: '',
    dotNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    firstName: '',
    lastName: '',
    title: '',
    phone: '',
    email: '',
    autoLiability: '',
    cargoInsurance: '',
    insuranceExpiration: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;

      const { data: appData } = await supabase
        .from('applications')
        .select('form_data')
        .eq('user_id', session.user.id)
        .order('submitted_at', { ascending: false })
        .limit(1)
        .single();

      if (appData?.form_data) {
        const { companyInfo, contactInfo, insurance } = appData.form_data;
        setFormData({
          carrierName: companyInfo?.carrierName || '',
          mcNumber: companyInfo?.mcNumber || '',
          dotNumber: companyInfo?.dotNumber || '',
          address: companyInfo?.address || '',
          city: companyInfo?.city || '',
          state: companyInfo?.state || '',
          zipCode: companyInfo?.zipCode || '',
          firstName: contactInfo?.firstName || '',
          lastName: contactInfo?.lastName || '',
          title: contactInfo?.title || '',
          phone: contactInfo?.phone || '',
          email: contactInfo?.email || '',
          autoLiability: insurance?.autoLiability || '',
          cargoInsurance: insurance?.cargoInsurance || '',
          insuranceExpiration: insurance?.expirationDate || '',
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) throw new Error('Not authenticated');

      const { data: appData } = await supabase
        .from('applications')
        .select('id, form_data')
        .eq('user_id', session.user.id)
        .order('submitted_at', { ascending: false })
        .limit(1)
        .single();

      if (!appData) throw new Error('No application found');

      const updatedFormData = {
        ...appData.form_data,
        companyInfo: {
          ...appData.form_data.companyInfo,
          carrierName: formData.carrierName,
          mcNumber: formData.mcNumber,
          dotNumber: formData.dotNumber,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
        },
        contactInfo: {
          ...appData.form_data.contactInfo,
          firstName: formData.firstName,
          lastName: formData.lastName,
          title: formData.title,
          phone: formData.phone,
          email: formData.email,
        },
        insurance: {
          ...appData.form_data.insurance,
          autoLiability: formData.autoLiability,
          cargoInsurance: formData.cargoInsurance,
          expirationDate: formData.insuranceExpiration,
        },
      };

      const { error: appError } = await supabase
        .from('applications')
        .update({ form_data: updatedFormData })
        .eq('id', appData.id);

      if (appError) throw appError;

      await supabase
        .from('profiles')
        .update({
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone_number: formData.phone,
          company_name: formData.carrierName,
          mc_number: formData.mcNumber,
          dot_number: formData.dotNumber,
        })
        .eq('id', session.user.id);

      setMessage('Profile updated successfully!');
      setTimeout(() => navigate('/carrier/dashboard'), 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const states = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><p>Loading...</p></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-900">Edit Profile</h1>
          <button onClick={() => navigate('/carrier/dashboard')} className="text-gray-600 hover:text-gray-900">
            ← Back to Dashboard
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Company Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Carrier Name *</label>
                <input type="text" name="carrierName" value={formData.carrierName} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">MC Number *</label>
                <input type="text" name="mcNumber" value={formData.mcNumber} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">DOT Number *</label>
                <input type="text" name="dotNumber" value={formData.dotNumber} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select name="state" value={formData.state} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                  <option value="">Select State</option>
                  {states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input type="text" name="title" value={formData.title} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
            </div>
          </div>

          {/* Insurance Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Insurance Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Auto Liability ($)</label>
                <input type="number" name="autoLiability" value={formData.autoLiability} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cargo Insurance ($)</label>
                <input type="number" name="cargoInsurance" value={formData.cargoInsurance} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date</label>
                <input type="date" name="insuranceExpiration" value={formData.insuranceExpiration} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
            </div>
          </div>

          {message && (
            <p className={`text-center ${message.includes('success') ? 'text-green-600' : 'text-red-600'}`}>{message}</p>
          )}

          <div className="flex justify-end gap-4">
            <button type="button" onClick={() => navigate('/carrier/dashboard')} className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CarrierProfileEditPage;